import { Avatar, Link, Stack, TextField, Typography } from '@mui/material';
import { ICaseDetailsInputs } from 'API';
import {
  CustomCheckbox,
  FileUpload,
  PhoneNumber,
  RadioButtons,
} from 'Components';
import { yesNoOptions } from 'Constants';
import { Dictionary } from 'Dictionary';
import { DICTIONARY, FileCategories } from 'Enum';
import { useAppSelector, useDictionary } from 'Hooks';
import { Download } from 'Images';
import { IndexContext } from 'Modules/CaseDetails/CaseContext';
import { useContext, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { generateError } from 'Regex';
import { styleTextField } from 'Theme';
import { textFieldShrink } from 'Utils';
import { isFileExist } from 'Utils/isFileExist';
import {
  validateLettersAndNumbersOnly,
  validateLettersOnly,
} from 'Utils/validateLettersAndNumbersOnly';

export const GeneralDetails = () => {
  const { index, fieldId } = useContext(IndexContext);
  const {
    register,
    formState: { errors },
    getValues,
    clearErrors,
    setValue,
  } = useFormContext<ICaseDetailsInputs>();

  const filesList = useAppSelector(({ file: { files } }) => files);
  const fileCategories = useAppSelector(
    ({
      generalContent: {
        generalContent: { fileCategories },
      },
    }) => fileCategories
  );

  const dictionary = useDictionary(Dictionary, DICTIONARY.CASE_DETAILS);

  const baseName = `caseDetails.${index}.commanderDetailsSection`;

  const commanderSectionValues = getValues(
    `caseDetails.${index}.commanderDetailsSection`
  );

  const [hasNoCivilAddress, hasNoCommanderDocument, civilAddress] = useWatch({
    name: [
      `${baseName}.hasNoCivilAddress`,
      `${baseName}.hasNoCommanderDocument`,
      `${baseName}.civilAddress`,
    ],
  });

  const commanderDetailsSectionServiceError = errors.caseDetails
    ? errors.caseDetails[index]?.commanderDetailsSection
    : undefined;

  useEffect(() => {
    if (hasNoCivilAddress) {
      setValue(
        `${baseName}.civilAddress` as `caseDetails.0.commanderDetailsSection.civilAddress`,
        ''
      );
      clearErrors(
        `${baseName}.civilAddress` as `caseDetails.0.commanderDetailsSection.civilAddress`
      );
    }
  }, [clearErrors, setValue, baseName, hasNoCivilAddress]);

  return (
    <Stack gap='24px'>
      <TextField
        id={dictionary.fourthSectionFirstName}
        inputProps={{
          maxLength: '20',
        }}
        {...register(
          `${baseName}.firstName` as `caseDetails.0.commanderDetailsSection.firstName`
        )}
        label={dictionary.fourthSectionFirstName}
        onChange={(event: any) => validateLettersOnly(event)}
        {...textFieldShrink(commanderSectionValues?.firstName, true)}
        sx={styleTextField}
        role={
          Boolean(commanderDetailsSectionServiceError?.firstName)
            ? 'alert'
            : undefined
        }
        error={Boolean(commanderDetailsSectionServiceError?.firstName)}
        helperText={
          Boolean(commanderDetailsSectionServiceError?.firstName?.message) &&
          generateError(
            dictionary.fourthSectionFirstName,
            commanderDetailsSectionServiceError?.firstName?.message!
          )
        }
      />
      <TextField
        id={dictionary.fourthSectionLastName}
        inputProps={{
          maxLength: '20',
        }}
        {...register(
          `${baseName}.lastName` as `caseDetails.0.commanderDetailsSection.lastName`
        )}
        label={dictionary.fourthSectionLastName}
        onChange={(event: any) => validateLettersOnly(event)}
        {...textFieldShrink(commanderSectionValues?.lastName, true)}
        sx={styleTextField}
        role={
          Boolean(commanderDetailsSectionServiceError?.lastName)
            ? 'alert'
            : undefined
        }
        error={Boolean(commanderDetailsSectionServiceError?.lastName)}
        helperText={
          Boolean(commanderDetailsSectionServiceError?.lastName?.message) &&
          generateError(
            dictionary.fourthSectionLastName,
            commanderDetailsSectionServiceError?.lastName?.message!
          )
        }
      />
      <PhoneNumber
        phoneNumberFormName={`${baseName}.phoneNumber`}
        phoneNumberPrefixFormName={`${baseName}.phonePrefixRelationId`}
        phoneNumberLabel={dictionary.fourthSectionPhone}
      />
      <Stack gap='10px'>
        <TextField
          id={`${dictionary.fourthSectionAddress}-${index}`}
          inputProps={{
            maxLength: '35',
          }}
          {...register(
            `${baseName}.civilAddress` as `caseDetails.0.commanderDetailsSection.civilAddress`
          )}
          label={dictionary.fourthSectionAddress}
          onKeyDown={(event: any) => validateLettersAndNumbersOnly(event)}
          {...textFieldShrink(civilAddress)}
          disabled={hasNoCivilAddress}
          sx={styleTextField}
          role={
            Boolean(commanderDetailsSectionServiceError?.civilAddress)
              ? 'alert'
              : undefined
          }
          error={Boolean(commanderDetailsSectionServiceError?.civilAddress)}
          helperText={
            Boolean(
              commanderDetailsSectionServiceError?.civilAddress?.message
            ) &&
            generateError(
              dictionary.fourthSectionAddress,
              commanderDetailsSectionServiceError?.civilAddress?.message!
            )
          }
        />
        <CustomCheckbox
          name={
            `${baseName}.hasNoCivilAddress` as `caseDetails.0.commanderDetailsSection.hasNoCivilAddress`
          }
          label={dictionary.fourthSectionNoInfo}
        />
      </Stack>
      <Stack>
        <Stack gap='10px'>
          <Typography sx={{ fontWeight: 'bold' }}>
            {dictionary.fourthSectionCommanderAddFile}
          </Typography>
          <Stack
            direction='row'
            alignItems='center'
            gap='7px'
            sx={{
              cursor: 'pointer',
            }}
            onClick={async () => {
              window.open(
                'https://shikum-umb.mod.gov.il/media/ciadbwtu/testimony.pdf',
                '_blank'
              );
            }}
          >
            <Link>{dictionary.forthSectionCommanderDoc}</Link>
            <Avatar
              alt=''
              src={Download}
              sx={{ width: '18px', height: '18px' }}
            />
          </Stack>
          <Stack>
            <FileUpload
              caseDetailsId={fieldId}
              fileLabel={dictionary.sixthSectionFile}
              fileCategoryId={FileCategories.InjuryFromTheUnitOrCommandStaff}
              ariaLabel={dictionary.sixthSectionFile}
              dataRequired={!hasNoCommanderDocument}
              disabled={hasNoCommanderDocument}
            />
            {!isFileExist(
              filesList,
              FileCategories.InjuryFromTheUnitOrCommandStaff
            ) && (
              <CustomCheckbox
                name={`${baseName}.hasNoCommanderDocument`}
                label={dictionary.fourthSectionNoInfo}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
      <RadioButtons
        id={dictionary.fourthSectionCommanderDetailsIsWitness}
        name={`${baseName}.isAlsoWitness` as const}
        options={yesNoOptions}
      />
    </Stack>
  );
};
